import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState, type FormEvent } from 'react';
import { api, ApiError, fieldIssues } from '../api';
import { leaveGuarded, sameValues, useUnsavedGuard, validForm } from '../ui/unsaved';
import { ConflictNotice } from '../ui/ConflictNotice';
import { ibanLabel } from './format';
import { BANK_KEY, type BankAccount } from './types';

const errorText = (e: unknown, fallback: string) => (e instanceof ApiError ? (fieldIssues(e)[0]?.message ?? e.message) : fallback);

function AccountForm({ initial, label, pending, error, onSave, onCancel }: { initial: { iban: string; name: string }; label: string; pending: boolean; error: unknown; onSave: (body: { iban: string; name: string }) => Promise<unknown>; onCancel?: () => void }) {
  const [iban, setIban] = useState(initial.iban);
  const [name, setName] = useState(initial.name);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    // the error is shown by the form (from the mutation)
    onSave({ iban, name }).catch(() => undefined);
  };
  // owner 2026-10-23 21:14: leaving with a changed account asks first
  const guardRef = useUnsavedGuard({
    dirty: !sameValues({ iban, name }, initial),
    save: async (el) => {
      if (!validForm(el)) return false;
      await onSave({ iban, name });
      return true;
    },
  });
  return (
    <form ref={guardRef} className="fin-add" aria-label={label} onSubmit={submit}>
      <label className="field bank-iban">
        <span>Numer rachunku</span>
        <input value={iban} inputMode="numeric" data-no-select-all="" autoComplete="off" placeholder="PL00 0000 0000 0000 0000 0000 0000" onChange={(e) => setIban(e.target.value)} required />
      </label>
      <label className="field">
        <span>Nazwa (opcjonalnie)</span>
        <input value={name} maxLength={200} placeholder="np. ING firmowy" onChange={(e) => setName(e.target.value)} />
      </label>
      <button className="btn" type="submit" disabled={pending}>
        {onCancel ? 'Zapisz' : 'Dodaj rachunek'}
      </button>
      {onCancel && (
        <button className="btn ghost" type="button" onClick={onCancel}>
          Anuluj
        </button>
      )}
      {error != null && (
        <p className="alert bank-form-error" role="alert">
          {errorText(error, 'Nie udało się zapisać rachunku')}
        </p>
      )}
    </form>
  );
}

/**
 * "Rachunki" (F37, behind the screen's ⚙): the company's accounts whose statements are accepted; a statement of another
 * account is refused whole. Only roles with "Rachunki" (edit) may add or change them.
 */
export function BankAccountsSettings({ editable, onClose }: { editable: boolean; onClose: () => void }) {
  const queryClient = useQueryClient();
  const list = useQuery({ queryKey: [...BANK_KEY, 'accounts'], queryFn: () => api<{ accounts: BankAccount[] }>('/api/bank/accounts') });
  const [editing, setEditing] = useState<string | null>(null);
  const [formKey, setFormKey] = useState(0);
  const refresh = () => void queryClient.invalidateQueries({ queryKey: BANK_KEY });
  const create = useMutation({
    mutationFn: (body: { iban: string; name: string }) => api('/api/bank/accounts', { method: 'POST', body }),
    onSuccess: () => {
      setFormKey((k) => k + 1);
      refresh();
    },
  });
  const update = useMutation({
    mutationFn: ({ a, body }: { a: BankAccount; body: { iban: string; name: string } }) => api(`/api/bank/accounts/${a.id}`, { method: 'PATCH', body: { ...body, version: a.version } }),
    onSuccess: () => {
      setEditing(null);
      refresh();
    },
  });
  const accounts = list.data?.accounts ?? [];
  const box = useRef<HTMLElement>(null);
  return (
    <section ref={box} className="bank-accounts panel" aria-label="Rachunki firmy" data-feature-id="bank.accounts">
      <div className="section-head">
        <h2>Rachunki firmy</h2>
        <button className="btn small ghost" type="button" onClick={() => leaveGuarded(onClose, box.current)}>
          Zamknij
        </button>
      </div>
      <p className="muted">Wyciągi innych rachunków niż te poniżej są odrzucane w całości.</p>
      {list.data && accounts.length === 0 && <p className="muted">Nie ma jeszcze rachunku — dodaj rachunek firmowy w ING.</p>}
      {accounts.length > 0 && (
        <ul className="bank-account-list">
          {accounts.map((a) =>
            editing === a.id ? (
              <li key={a.id}>
                {update.error instanceof ApiError && update.error.status === 409 && (
                  <ConflictNotice
                    onReload={() => {
                      update.reset();
                      setEditing(null);
                      refresh();
                    }}
                  />
                )}
                <AccountForm label={`Zmiana rachunku ${a.name || a.iban}`} initial={{ iban: ibanLabel(a.iban), name: a.name }} pending={update.isPending} error={update.error instanceof ApiError && update.error.status === 409 ? null : update.error} onCancel={() => setEditing(null)} onSave={(body) => update.mutateAsync({ a, body })} />
              </li>
            ) : (
              <li key={a.id}>
                <span className="bank-iban-text">{ibanLabel(a.iban)}</span>
                {a.name && <span className="muted"> · {a.name}</span>}
                {editable && (
                  <button className="btn small ghost" type="button" onClick={() => leaveGuarded(() => setEditing(a.id), box.current?.querySelector('.bank-account-list'))}>
                    Zmień
                  </button>
                )}
              </li>
            ),
          )}
        </ul>
      )}
      {editable && <AccountForm key={formKey} label="Nowy rachunek" initial={{ iban: '', name: '' }} pending={create.isPending} error={create.error} onSave={(body) => create.mutateAsync(body)} />}
    </section>
  );
}
